import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from "react";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

interface ThemeContextValue {
    mode: ThemeMode;
    theme: ResolvedTheme;
    setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemTheme(): ResolvedTheme {
    const dl = window.DataLab;
    if (typeof dl?.isDarkMode === "function") {
        return dl.isDarkMode() ? "dark" : "light";
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
    return mode === "system" ? getSystemTheme() : mode;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [mode, setModeState] = useState<ThemeMode>(() => {
        const saved = localStorage.getItem("biostream-theme");
        if (saved === "light" || saved === "dark" || saved === "system")
            return saved;
        return "system";
    });

    const [theme, setTheme] = useState<ResolvedTheme>(() => resolveTheme(mode));

    const setMode = useCallback((m: ThemeMode) => {
        setModeState(m);
        localStorage.setItem("biostream-theme", m);
        setTheme(resolveTheme(m));
    }, []);

    // Listen for system theme changes
    useEffect(() => {
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = () => {
            if (mode === "system") {
                setTheme(getSystemTheme());
            }
        };
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, [mode]);

    // Apply theme class to document + scrollbar styles
    useEffect(() => {
        document.documentElement.dataset.theme = theme;
        document.documentElement.style.colorScheme = theme;

        const id = "biostream-scrollbar-css";
        let style = document.getElementById(id) as HTMLStyleElement | null;
        if (!style) {
            style = document.createElement("style");
            style.id = id;
            document.head.appendChild(style);
        }

        const c = theme === "dark" ? darkColors : lightColors;
        style.textContent = `
      /* Webkit (Chrome, Safari, Edge) */
      ::-webkit-scrollbar { width: 8px; height: 8px; }
      ::-webkit-scrollbar-track { background: ${c.bg}; }
      ::-webkit-scrollbar-thumb { background: ${c.borderDark}; border-radius: 4px; }
      ::-webkit-scrollbar-thumb:hover { background: ${c.textMuted}; }
      ::-webkit-scrollbar-corner { background: ${c.bg}; }

      /* Firefox */
      * { scrollbar-width: thin; scrollbar-color: ${c.borderDark} ${c.bg}; }
    `;
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ mode, theme, setMode }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
    return ctx;
}

// ─── Colors ────────────────────────────────────────────────────────────

const lightColors = {
  primary: '#356253',
  danger: '#DC2626',
  success: '#04B56D',
  warning: '#F29B00',
  text: '#111827',
  textMuted: '#6B7280',
  bg: '#FFFFFF',
  bgAlt: '#F9FAFB',
  border: '#E5E7EB',
  borderDark: '#D1D5DB',
  formInk: '#0A4A89',
  triageI: '#EA0F0F',
  triageII: '#FCB903',
  triageIII: '#319060',
  triageIV: '#232323',
};

const darkColors = {
  primary: '#429474',
  danger: '#EF4444',
  success: '#04B56D',
  warning: '#F95900',
  text: '#9A9A9A',
  textMuted: '#D4D4D4',
  bg: '#2B2B2B',
  bgAlt: '#4D4D4D',
  border: '#424242',
  borderDark: '#707070',
  formInk: '#24BEC6',
  triageI: '#BD4036',
  triageII: '#FCB903',
  triageIII: '#319060',
  triageIV: '#000000',
};

export type ThemeColors = typeof lightColors;

export function getColors(theme: ResolvedTheme): ThemeColors {
    return theme === "dark" ? darkColors : lightColors;
}

// ─── Audit domain colors ────────────────────────────────────────────────

const domainColorsLight: Record<string, string> = {
    region: "#0d9488",
    hospital: "#2563eb",
    unit: "#7c3aed",
    user: "#16a34a",
    session: "#6b7280",
    patient: "#ea580c",
    case: "#dc2626",
    vitals: "#db2777",
    ventilation: "#0891b2",
    diagnostics: "#4f46e5",
    labValues: "#d97706",
    exam: "#0f766e",
    cpr: "#b91c1c",
    tourniquet: "#9f1239",
    medication: "#15803d",
    surgery: "#6d28d9",
    attachment: "#475569",
    system: "#1e293b",
};

const domainColorsDark: Record<string, string> = {
    region: "#2dd4bf",
    hospital: "#60a5fa",
    unit: "#a78bfa",
    user: "#4ade80",
    session: "#9ca3af",
    patient: "#fb923c",
    case: "#f87171",
    vitals: "#f472b6",
    ventilation: "#22d3ee",
    diagnostics: "#818cf8",
    labValues: "#fbbf24",
    exam: "#5eead4",
    cpr: "#fca5a5",
    tourniquet: "#fda4af",
    medication: "#4ade80",
    surgery: "#a78bfa",
    attachment: "#94a3b8",
    system: "#cbd5e1",
};

export function getDomainColors(theme: ResolvedTheme): Record<string, string> {
    return theme === "dark" ? domainColorsDark : domainColorsLight;
}

// ─── Theme Switcher Component ──────────────────────────────────────────

export function ThemeSwitcher() {
    const { mode, setMode, theme } = useTheme();
    const colors = getColors(theme);

    const modes: ThemeMode[] = ["light", "dark", "system"];
    const icons: Record<ThemeMode, string> = {
        light: "\u2600", // sun
        dark: "\u263D", // moon
        system: "\u25D0", // circle half
    };

    return (
        <span
            style={{
                display: "inline-flex",
                border: `1px solid ${colors.border}`,
                borderRadius: 4,
                overflow: "hidden",
            }}
        >
            {modes.map((m) => (
                <button
                    key={m}
                    onClick={() => setMode(m)}
                    title={m}
                    style={{
                        padding: "1px 4px",
                        border: "none",
                        cursor: "pointer",
                        background: mode === m ? colors.primary : "transparent",
                        color: mode === m ? "#fff" : colors.textMuted,
                        fontSize: "0.875rem",
                        lineHeight: 1,
                    }}
                >
                    {icons[m]}
                </button>
            ))}
        </span>
    );
}
